import { Injectable, computed, inject } from '@angular/core'

import {
  Enums,
  Hunt,
  ExpandedHuntPackageRecord,
} from '@cyborg-monorepo/hunter/common'
import { ProgressSegment } from '@hyperkit'

import { formatSafeDate } from '../../../../helpers/utils'
import { outcomeMap } from '../../helpers/hunt-badge-variants'
import {
  getPriorityVariant,
  getStatusVariant,
  getOutcomeVariant,
} from '../../helpers/hunt-badge-variants'
import { countQueriesInPackage } from '../../helpers/query-status-helper'
import { NavigationService } from '../navigation.service'
import { SessionService } from '../session.service'

/**
 * HuntViewService provides read-only views over the current Hunt,
 * exposing derived properties via signals.
 */
@Injectable({ providedIn: 'root' })
export class HuntViewService {
  private readonly session = inject(SessionService)
  private readonly nav = inject(NavigationService)

  readonly huntId = computed<string | null>(() => this.nav.huntId())
  readonly activeHunt = computed<Hunt | null>(() => this.session.activeHunt())

  readonly huntPackages = computed<ExpandedHuntPackageRecord[]>(
    () => this.activeHunt()?.huntPackages ?? [],
  )

  readonly huntQueryStatusCounts = computed(() => {
    return this.huntPackages().reduce(
      (acc, pkg) => {
        const counts = countQueriesInPackage(pkg)
        acc.toDo += counts.toDo
        acc.run += counts.run
        acc.skip += counts.skip
        return acc
      },
      { toDo: 0, run: 0, skip: 0 },
    )
  })

  readonly totalHuntQueries = computed(() => {
    const c = this.huntQueryStatusCounts()
    return c.toDo + c.run + c.skip
  })

  readonly huntProgressSegments = computed<ProgressSegment[]>(() => {
    const { toDo, run, skip } = this.huntQueryStatusCounts()
    return [
      { value: run, color: 'success', label: 'Run' },
      { value: skip, color: 'info', label: 'Skip' },
      { value: toDo, color: 'secondary', label: 'To Do' },
    ]
  })

  readonly isComplete = computed<boolean>(
    () => this.activeHunt()?.status === Enums.StatusEnum.DONE,
  )

  readonly lastModified = computed(() =>
    formatSafeDate(this.activeHunt()?.lastModified),
  )

  readonly isArchived = computed<boolean>(
    () => this.activeHunt()?.archived ?? false,
  )

  readonly assigneeFullName = computed<string>(() => {
    const a = this.activeHunt()?.assignee
    return a ? `${a.firstName} ${a.lastName}` : 'Unassigned'
  })

  readonly huntTemplateName = computed<string>(
    () => this.activeHunt()?.huntTemplate?.name ?? 'No Template Name',
  )

  readonly hasHuntTemplate = computed<boolean>(
    () => this.activeHunt()?.huntTemplate != null,
  )

  readonly notes = computed<string>(() => this.activeHunt()?.notes ?? '')
  readonly abstract = computed<string>(() => this.activeHunt()?.abstract ?? '')
  readonly outcome = computed<string>(() => this.activeHunt()?.outcome ?? '')
  readonly name = computed<string>(
    () => this.activeHunt()?.name ?? 'Untitled Hunt',
  )

  readonly createdDate = computed(() =>
    formatSafeDate(this.activeHunt()?.created),
  )

  readonly authorFullName = computed<string>(() => {
    const au = this.activeHunt()?.author
    return au ? `${au.firstName} ${au.lastName}` : 'Unknown'
  })

  readonly priority = computed<Enums.PriorityEnum>(
    () => this.activeHunt()?.priority ?? Enums.PriorityEnum.LOW,
  )
  readonly priorityVariant = computed(() => getPriorityVariant(this.priority()))

  readonly status = computed<Enums.StatusEnum>(
    () => this.activeHunt()?.status ?? Enums.StatusEnum.IN_PROGRESS,
  )
  readonly statusVariant = computed(() => getStatusVariant(this.status()))

  readonly outcomeVariant = computed(() =>
    getOutcomeVariant(this.outcome() as Enums.OutcomeEnum),
  )

  readonly conclusion = computed<string>(
    () => this.activeHunt()?.conclusion ?? '',
  )

  readonly outcomeDependentField = computed<string>(
    () =>
      outcomeMap.get(this.activeHunt()?.outcome ?? null) ?? 'Hunt Methodology',
  )

  readonly outcomeDependent = computed<string>(
    () => this.activeHunt()?.outcomeDependent ?? '',
  )

  readonly executiveSummary = computed<string>(() => {
    const s = this.activeHunt()?.executiveSummary
    return s ? s.trim() : 'No executive summary provided.'
  })

  readonly hypothesis = computed<string>(
    () => this.activeHunt()?.hypothesis ?? '',
  )

  readonly huntTemplateId = computed<string | null>(
    () => this.activeHunt()?.huntTemplate?.id ?? null,
  )
}
